import { useMemo } from "react";

import { AndroidDark, AndroidLight, AppStoreDark, AppStoreLight, GetItOnPlayStoreDark, GetItOnPlayStoreLight, ROUTES } from "@/constants";
import useDownloadMobileApplicationsConfig from "@/features/mobileApplications/useDownloadMobileApplicationsConfig";
import { useBrand, useTheme } from "@/hooks";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";

type NavigationApplicationBadge = {
  logo: string;
  width: number;
  height: number;
  to: string;
  label: string;
  alt: string;
  onPress: () => void;
};

// Match SVG intrinsic sizes so object-cover does not clip edge strokes
// (Play badges inset their border; App Store light draws it on the viewBox edge).
const BADGE_HEIGHT = 30;
const PLAY_STORE_BADGE_WIDTH = 97;
const APP_STORE_BADGE_WIDTH_LIGHT = 94;
const APP_STORE_BADGE_WIDTH_DARK = 97;

type UseNavigationApplicationBadgesParams = {
  applicationName: string | undefined;
  onPress: () => void;
};

export default function useNavigationApplicationBadges({
  applicationName,
  onPress,
}: UseNavigationApplicationBadgesParams): NavigationApplicationBadge[] {
  const { androidApplication, iosApplication } = useDownloadMobileApplicationsConfig();
  const { isPixelChat } = useBrand();
  const { isDark } = useTheme();
  const localizePathname = useLocalizedPathname();
  const playStoreBadgeDark = isPixelChat ? GetItOnPlayStoreDark : AndroidDark;
  const playStoreBadgeLight = isPixelChat ? GetItOnPlayStoreLight : AndroidLight;
  const appStoreBadge = isDark ? AppStoreDark : AppStoreLight;
  const appStoreBadgeWidth = isDark ? APP_STORE_BADGE_WIDTH_DARK : APP_STORE_BADGE_WIDTH_LIGHT;
  const downloadPath = localizePathname(ROUTES.download);

  return useMemo(() => {
    const badges: NavigationApplicationBadge[] = [];

    if (androidApplication) {
      badges.push({
        logo: isDark ? playStoreBadgeDark : playStoreBadgeLight,
        width: PLAY_STORE_BADGE_WIDTH,
        height: BADGE_HEIGHT,
        to: downloadPath,
        label: "playstore-logo",
        alt: `Download ${applicationName} on Android`,
        onPress,
      });
    }

    if (iosApplication) {
      badges.push({
        logo: appStoreBadge,
        width: appStoreBadgeWidth,
        height: BADGE_HEIGHT,
        to: downloadPath,
        label: "appstore-logo",
        alt: `Download ${applicationName} on iOS`,
        onPress,
      });
    }

    return badges;
  }, [
    androidApplication,
    appStoreBadge,
    appStoreBadgeWidth,
    applicationName,
    downloadPath,
    iosApplication,
    isDark,
    onPress,
    playStoreBadgeDark,
    playStoreBadgeLight,
  ]);
}
